import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'

import 'video.js/dist/video-js.css'
import {
  type VideoJSIVSTech,
  type VideoJSQualityPlugin,
  registerIVSQualityPlugin,
  registerIVSTech,
} from 'amazon-ivs-player'
import { ErrorType } from 'amazon-ivs-player'
// @ts-expect-error – provided by package
import wasmWorkerPath from 'amazon-ivs-player/dist/assets/amazon-ivs-wasmworker.min.js'
// @ts-expect-error – provided by package
import wasmBinaryPath from 'amazon-ivs-player/dist/assets/amazon-ivs-wasmworker.min.wasm'
import styled from 'styled-components'
import videojs from 'video.js'

import { useViewerStore } from 'store/viewer'

type PlayerType = ReturnType<typeof videojs>

const toAbs = (p: string) => new URL(p, document.URL).toString()

let ivsTechRegistered = false

const ensureIvsTechRegistered = () => {
  if (ivsTechRegistered) return

  registerIVSTech(videojs, {
    wasmWorker: toAbs(wasmWorkerPath),
    wasmBinary: toAbs(wasmBinaryPath),
  })

  registerIVSQualityPlugin(videojs)

  ivsTechRegistered = true
}

type Props = {
  source: string
  onEnded?: () => void
  onFatalError?: (err?: unknown) => void
  autoplayMuted?: boolean
}

export type StreamPlayerHandle = {
  stop: () => void
}

export const StreamPlayer = forwardRef<StreamPlayerHandle, Props>(
  ({ source, onEnded, onFatalError, autoplayMuted = true }, ref) => {
    StreamPlayer.displayName = 'StreamPlayer'

    const videoElRef = useRef<HTMLVideoElement | null>(null)
    const playerRef = useRef<(PlayerType & VideoJSIVSTech & VideoJSQualityPlugin) | null>(null)
    const muted = useViewerStore(s => s.muted)

    useImperativeHandle(ref, () => ({
      stop() {
        const p = playerRef.current
        try { p?.pause() } catch {}
        try { p?.src({ src: '', type: 'application/x-mpegURL' }) } catch {}
      },
    }), [])
console.log('player.tsx')
    useEffect(() => {
      if (playerRef.current) return
      const el = videoElRef.current
      if (!el) return

      ensureIvsTechRegistered()

      const player = videojs(el, {
        techOrder: ['AmazonIVS'],
        autoplay: true,
        muted: autoplayMuted,
        controls: false,
        fill: true,
        responsive: true,
        liveui: true,
        playsinline: true,
        controlBar: { volumePanel: { inline: false } },
      }) as PlayerType & VideoJSIVSTech & VideoJSQualityPlugin

      player.on('error', () => {
        onFatalError?.(player.error())
      })

      try { player.enableIVSQualityPlugin() } catch {}

      try {
        const ivsPlayer = player.getIVSPlayer()
        const events = player.getIVSEvents()

        const handleEnded = () => {
          try { player.pause() } catch {}
          try { player.src({ src: '', type: 'application/x-mpegURL' }) } catch {}
          onEnded?.()
        }

        const handleError = (err: any) => {
          const errType = err?.type
          const code = err?.code ?? err?.errorCode

          if (
            errType === ErrorType?.NOT_AVAILABLE ||
            errType === 'NOT_AVAILABLE' ||
            code === 404 ||
            code === 410 ||
            code === 403
          ) {
            handleEnded()
            return
          }

          onFatalError?.(err)
        }

        if (events.PlayerState.ENDED) {
          ivsPlayer.addEventListener(events.PlayerState.ENDED, handleEnded)
        } else {
          let wasPlaying = false
          ivsPlayer.addEventListener(events.PlayerState.PLAYING, () => { wasPlaying = true })
          ivsPlayer.addEventListener(events.PlayerState.IDLE, () => {
            if (wasPlaying) handleEnded()
          })
        }

        ivsPlayer.addEventListener(events.PlayerEventType.ERROR, handleError)

        ;(player as any).__ivsCleanup = () => {
          try {
            if (events.PlayerState.ENDED) {
              ivsPlayer.removeEventListener(events.PlayerState.ENDED, handleEnded)
            }
          } catch {}
          try {
            ivsPlayer.removeEventListener(events.PlayerEventType.ERROR, handleError)
          } catch {}
        }
      } catch {}

      playerRef.current = player
    }, [autoplayMuted, onEnded, onFatalError])

    useEffect(() => {
      const p = playerRef.current
      if (!p) return

      try {
        if (source) {
          p.src({ src: source, type: 'application/x-mpegURL' })
          ;(p as any).play?.()
        } else {
          p.pause()
          p.src({ src: '', type: 'application/x-mpegURL' })
        }
      } catch (e) {
        onFatalError?.(e)
      }
    }, [source, onFatalError])

    useEffect(() => {
      const p = playerRef.current
      if (!p) return
      try { p.muted(muted) } catch {}
    }, [muted])

    useEffect(() => {
      return () => {
        const p = playerRef.current
        try { (p as any)?.__ivsCleanup?.() } catch {}
        try { if (p && !p.isDisposed()) p.dispose() } catch {}
        playerRef.current = null
      }
    }, [])

    return (
      <Wrapper>
        <Video
          ref={videoElRef}
          className="video-js vjs-default-skin vjs-big-play-centered"
          autoPlay
          playsInline
        />
      </Wrapper>
    )
  }
)

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
  max-height: 100vh;
  background: #000;
`

const Video = styled.video`
  display: block;
  width: 100%;
  height: 100%;
  background: transparent;

  .vjs-tech,
  & {
    object-fit: contain;
  }
`